import { getLogger } from 'app-components/utils/core';
import { getTxnDifferences } from 'data/transactions/utils';

const log = getLogger('data/transactionList/optimalCacheFunctions');

/*
  getTransactionListForAccountIds
*/

// Attempt to process newTxn into the cache
// returns the new cache, or NULL if a recalc is needed (can't resolve the cache)
export const optimalCacheProcessFn = (cachedValues, newTxn, filterFn) => {
  let txIndex = -1;
  let sectionKey = null;
  cachedValues.forEach((sec, key) => {
    const index = sec.findIndex((txn) => txn.id === newTxn.id);
    if (index !== -1) {
      sectionKey = key;
      txIndex = index;
    }
  });
  // console.log("PROCESSING looking for ", newTxn);
  if (txIndex !== -1 && sectionKey) {
    // console.log("FOUND TXN AT ", sectionKey, txIndex, cachedValues.get(sectionKey));
    // check for deleted or whether this txn was filtered out of the list

    // console.log("New Txn Filter Test ", newTxn, filterFn && filterFn([newTxn]));
    if (newTxn.isDeleted || (filterFn && filterFn([newTxn]).length === 0)) {
      return cachedValues.set(sectionKey, cachedValues.get(sectionKey).remove(txIndex));
    }

    return cachedValues.setIn([sectionKey, txIndex], newTxn);
  }
  return newTxn.isDeleted ? cachedValues : null; // if new txn was deleted, and not found, that is ok
};

// Returns TRUE if the changes do NOT cause a recalc of the selector and allow
// for cache replacement
export const optimalCacheCompareFn = (oldTxn, newTxn, sortBy, filterFn) => {
  // determine which fields changed
  const diffs = getTxnDifferences(oldTxn.toJS(), newTxn.toJS());
  log.debug('TXNlist DIFFS ARE ', diffs, sortBy);
  if (filterFn && filterFn([newTxn]).length === 0) {
    return true; // we will delete this transaction in the process function
  }
  return (
    !diffs.includes('postedOn') && // always resort on date or amount change
    !(
      (sortBy === 'status' || sortBy === 'state' || sortBy === 'date' || sortBy === 'postedOn') &&
      diffs.includes('state')
    ) && // TODO: remove 'status' and 'date' after register deprecated (deprecatedRegister)
    !(sortBy === 'amount' && diffs.includes('amount')) &&
    !(sortBy === 'reviewed' && diffs.includes('isReviewed')) &&
    !(sortBy === 'category' && diffs.includes('coa')) &&
    !(sortBy === 'payee' && diffs.includes('payee'))
  );
};

/*
  Recent Transactions
*/

// this is for when the value is wrapped in a JS object (so the object pointer does not change)
export const optimalCacheProcessFnRecentAcctTxns = (cachedObject, newTxn) => {
  assert(cachedObject?.immutableValue, 'optimalCacheProcessFnRecent was called without an immutable value');
  // eslint-disable-next-line no-param-reassign
  cachedObject.immutableValue = optimalCacheProcessFnTxnGrouped(cachedObject.immutableValue, newTxn);
  return cachedObject;
};

/*
  General Process Grouped Txns
*/
export const optimalCacheProcessFnTxnGrouped = (cachedValues, newTxn) => {
  let txIndex = -1;
  let sectionKey = null;

  cachedValues.forEach((sec, key) => {
    const foundTxn = sec.find((txn) => txn.id === newTxn.id);
    if (foundTxn) {
      sectionKey = key;
      txIndex = foundTxn.id;
    }
  });
  if (txIndex !== -1 && sectionKey) {
    log.debug('FOUND TXN AT ', sectionKey, txIndex, cachedValues.get(sectionKey));
    // check for deleted
    if (newTxn.isDeleted) return cachedValues.set(sectionKey, cachedValues.get(sectionKey).delete(txIndex));
    return cachedValues.setIn([sectionKey, txIndex], newTxn);
  }
  return cachedValues;
};

export const optimalCacheCompareFnFactory = (fields) => (oldTxn, newTxn) => {
  // which fields changed?
  const diff = getTxnDifferences(oldTxn.toJS(), newTxn.toJS());

  // have fields we are interested in changed?
  return !fields.some((field) => diff.includes(field));
};

/*
  General Compare PostedOn
*/
export const optimalCacheCompareFnPostedOn = optimalCacheCompareFnFactory(['postedOn', 'state']);

export const optimalCacheCompareFnBubble = optimalCacheCompareFnFactory([
  'postedOn',
  'amount',
  'coa',
  'payee',
  'isExcludedFromReports',
  'isExcludedFromF2S',
]);

/*
  General Process Txn List
*/
export const optimalCacheProcessFnTxnList = (cachedValues, newTxn) => {
  const txIndex = cachedValues.findIndex((txn) => txn.id === newTxn.id);

  if (txIndex !== -1) {
    // check for deleted
    if (newTxn.isDeleted) return cachedValues.delete(txIndex);
    return cachedValues.set(txIndex, newTxn);
  }
  return cachedValues; // if new txn was deleted, and not found, that is ok
};
